import React, { useState, useMemo, useRef, useEffect } from "react";
import { Download, RefreshCw, Search, Trophy } from "lucide-react";
import svgPaths from "../../imports/svg-uklt2414pv";

/* ── Types ─────────────────────────────────────── */

interface Member {
  initials: string;
  color: string;
}

interface LeaderboardEntry {
  rank: number;
  name: string;
  type: "team" | "solo";
  members: Member[];
  score: number;
  submittedAt: string;
}

/* ── Low-saturation avatar palette ─────────────── */

const AVATAR_COLORS = [
  "#8898A8", // muted slate-blue
  "#88A090", // muted sage
  "#A89878", // muted sand
  "#9888A8", // muted lavender
  "#A89088", // muted terracotta
  "#8898A0", // muted teal
  "#A89090", // muted rose
  "#98A080", // muted olive
  "#7B8EA8", // dusty steel
  "#A89A80", // warm greige
  "#90A08A", // grey-green
  "#A08890", // dusty mauve
];

/* ── Mock data ──────────────────────────────────── */

const LEADERBOARD: LeaderboardEntry[] = [
  {
    rank: 1, name: "Synapse75", type: "team",
    members: [{ initials: "DS", color: AVATAR_COLORS[0] }, { initials: "TW", color: AVATAR_COLORS[4] }],
    score: 7.168, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 2, name: "Dominic Li", type: "solo",
    members: [{ initials: "DL", color: AVATAR_COLORS[1] }],
    score: 7.161, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 3, name: "liningsite428", type: "solo",
    members: [{ initials: "LS", color: AVATAR_COLORS[2] }],
    score: 7.153, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 4, name: "Javier_54", type: "solo",
    members: [{ initials: "JV", color: AVATAR_COLORS[3] }],
    score: 7.148, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 5, name: "TeamNet", type: "team",
    members: [{ initials: "TN", color: AVATAR_COLORS[4] }, { initials: "AR", color: AVATAR_COLORS[0] }, { initials: "NC", color: AVATAR_COLORS[1] }],
    score: 7.138, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 6, name: "XIKAI PAN", type: "team",
    members: [{ initials: "XP", color: AVATAR_COLORS[11] }, { initials: "MK", color: AVATAR_COLORS[5] }],
    score: 7.132, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 7, name: "jiachengjiacheng", type: "solo",
    members: [{ initials: "JC", color: AVATAR_COLORS[10] }],
    score: 7.124, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 8, name: "onaijin", type: "solo",
    members: [{ initials: "ON", color: AVATAR_COLORS[6] }],
    score: 7.117, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 9, name: "Nathan Kamuta", type: "solo",
    members: [{ initials: "NK", color: AVATAR_COLORS[7] }],
    score: 7.110, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 10, name: "John Zhang", type: "solo",
    members: [{ initials: "JZ", color: AVATAR_COLORS[8] }],
    score: 7.102, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 11, name: "Guntas Dhanjal", type: "solo",
    members: [{ initials: "GD", color: AVATAR_COLORS[9] }],
    score: 7.098, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 12, name: "Rk–", type: "solo",
    members: [{ initials: "RK", color: AVATAR_COLORS[2] }],
    score: 7.088, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 13, name: "DataWizard_X", type: "team",
    members: [
      { initials: "DX", color: AVATAR_COLORS[3] }, { initials: "SL", color: AVATAR_COLORS[0] },
      { initials: "KM", color: AVATAR_COLORS[11] }, { initials: "YP", color: AVATAR_COLORS[5] },
    ],
    score: 7.080, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 14, name: "MLProdigy", type: "solo",
    members: [{ initials: "MP", color: AVATAR_COLORS[10] }],
    score: 7.073, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 15, name: "NeuralNinja", type: "team",
    members: [
      { initials: "NN", color: AVATAR_COLORS[6] }, { initials: "AK", color: AVATAR_COLORS[8] },
      { initials: "BW", color: AVATAR_COLORS[1] }, { initials: "CL", color: AVATAR_COLORS[9] },
      { initials: "DM", color: AVATAR_COLORS[4] },
    ],
    score: 7.066, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 16, name: "AlphaCode", type: "solo",
    members: [{ initials: "AC", color: AVATAR_COLORS[3] }],
    score: 7.059, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 17, name: "BetaBridge", type: "team",
    members: [{ initials: "BB", color: AVATAR_COLORS[4] }, { initials: "RC", color: AVATAR_COLORS[11] }],
    score: 7.051, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 18, name: "GammaSolver", type: "solo",
    members: [{ initials: "GS", color: AVATAR_COLORS[5] }],
    score: 7.044, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 19, name: "DeltaMind", type: "team",
    members: [
      { initials: "DM", color: AVATAR_COLORS[10] }, { initials: "EL", color: AVATAR_COLORS[7] },
      { initials: "FN", color: AVATAR_COLORS[2] },
    ],
    score: 7.037, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 20, name: "EpsilonAI", type: "solo",
    members: [{ initials: "EA", color: AVATAR_COLORS[0] }],
    score: 7.029, submittedAt: "Apr 28, 2026  14:30",
  },
  {
    rank: 21, name: "QuantumLeap", type: "team",
    members: [{ initials: "QL", color: AVATAR_COLORS[8] }, { initials: "PV", color: AVATAR_COLORS[6] }, { initials: "TX", color: AVATAR_COLORS[1] }],
    score: 7.021, submittedAt: "Apr 28, 2026  14:28",
  },
  {
    rank: 22, name: "SigmaStar", type: "solo",
    members: [{ initials: "SS", color: AVATAR_COLORS[9] }],
    score: 7.015, submittedAt: "Apr 28, 2026  14:27",
  },
  {
    rank: 23, name: "OmegaTeam", type: "team",
    members: [{ initials: "OT", color: AVATAR_COLORS[3] }, { initials: "MR", color: AVATAR_COLORS[11] }],
    score: 7.009, submittedAt: "Apr 28, 2026  14:25",
  },
  {
    rank: 24, name: "ZetaForce", type: "solo",
    members: [{ initials: "ZF", color: AVATAR_COLORS[4] }],
    score: 7.002, submittedAt: "Apr 28, 2026  14:22",
  },
  {
    rank: 25, name: "EtaGroup", type: "team",
    members: [
      { initials: "EG", color: AVATAR_COLORS[2] }, { initials: "WL", color: AVATAR_COLORS[5] },
      { initials: "XK", color: AVATAR_COLORS[10] }, { initials: "JD", color: AVATAR_COLORS[7] },
    ],
    score: 6.998, submittedAt: "Apr 28, 2026  14:20",
  },
  {
    rank: 26, name: "ThetaMinds", type: "solo",
    members: [{ initials: "TM", color: AVATAR_COLORS[0] }],
    score: 6.991, submittedAt: "Apr 28, 2026  14:18",
  },
  {
    rank: 27, name: "IotaLab", type: "team",
    members: [{ initials: "IL", color: AVATAR_COLORS[9] }, { initials: "NK", color: AVATAR_COLORS[1] }],
    score: 6.985, submittedAt: "Apr 28, 2026  14:16",
  },
  {
    rank: 28, name: "KappaWave", type: "solo",
    members: [{ initials: "KW", color: AVATAR_COLORS[6] }],
    score: 6.979, submittedAt: "Apr 28, 2026  14:14",
  },
  {
    rank: 29, name: "LambdaNet", type: "team",
    members: [{ initials: "LN", color: AVATAR_COLORS[4] }, { initials: "SY", color: AVATAR_COLORS[3] }, { initials: "RB", color: AVATAR_COLORS[8] }],
    score: 6.972, submittedAt: "Apr 28, 2026  14:11",
  },
  {
    rank: 30, name: "MuSolver", type: "solo",
    members: [{ initials: "MS", color: AVATAR_COLORS[11] }],
    score: 6.966, submittedAt: "Apr 28, 2026  14:09",
  },
];

const PAGE_SIZES = [20, 50, 100];

/* ── Avatar component ───────────────────────────── */

function Avatar({ initials, color, size = 26 }: { initials: string; color: string; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        border: "1.5px solid var(--brand-bg)",
      }}
    >
      <span
        className="font-['Helvetica_Neue:Bold',sans-serif] leading-none"
        style={{ fontSize: size * 0.38, color: "rgba(255,255,255,0.92)", letterSpacing: "0.02em" }}
      >
        {initials}
      </span>
    </div>
  );
}

/* ── Member avatars stack ───────────────────────── */

function MemberAvatars({ members }: { members: Member[] }) {
  return (
    <div className="flex items-center" style={{ gap: 0 }}>
      {members.map((m, i) => (
        <div key={i} style={{ marginLeft: i === 0 ? 0 : -6, zIndex: members.length - i }}>
          <Avatar initials={m.initials} color={m.color} />
        </div>
      ))}
    </div>
  );
}

/* ── Rank display ───────────────────────────────── */

function RankCell({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="flex items-center gap-[6px]">
        <Trophy size={13} style={{ color: "#B8922A" }} />
        <span className="font-['Helvetica_Neue:Bold',sans-serif] text-[14px]" style={{ color: "#B8922A" }}>1</span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex items-center gap-[6px]">
        <Trophy size={13} style={{ color: "#8898A8" }} />
        <span className="font-['Helvetica_Neue:Bold',sans-serif] text-[14px]" style={{ color: "#8898A8" }}>2</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex items-center gap-[6px]">
        <Trophy size={13} style={{ color: "#A08060" }} />
        <span className="font-['Helvetica_Neue:Bold',sans-serif] text-[14px]" style={{ color: "#A08060" }}>3</span>
      </div>
    );
  }
  return (
    <span
      className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
      style={{ color: "var(--brand-text)", opacity: 0.45 }}
    >
      {String(rank).padStart(2, "0")}
    </span>
  );
}

/* ── Team/Solo badge ────────────────────────────── */

function TypeBadge({ type }: { type: "team" | "solo" }) {
  const isTeam = type === "team";
  return (
    <span
      className="inline-flex items-center px-[5px] py-[1px] font-['Helvetica_Neue:Bold',sans-serif] tracking-[0.7px] shrink-0 text-[10px]"
      style={{
        backgroundColor: isTeam
          ? "rgba(59,14,39,0.08)"
          : "rgba(59,14,39,0.04)",
        border: isTeam
          ? "1px solid rgba(59,14,39,0.16)"
          : "1px solid rgba(59,14,39,0.08)",
        color: isTeam
          ? "var(--brand-text)"
          : "var(--brand-text)",
        opacity: isTeam ? 0.75 : 0.38,
      }}
    >
      {isTeam ? "Team" : "Solo"}
    </span>
  );
}

/* ── Leaderboard Tab ────────────────────────────── */

export function LeaderboardTab() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return LEADERBOARD;
    const q = search.toLowerCase();
    return LEADERBOARD.filter(e => e.name.toLowerCase().includes(q));
  }, [search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filtered.length);

  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
    setPageSizeOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!pageSizeOpen) return;
    const handler = (e: MouseEvent) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(e.target as Node)) {
        setPageSizeOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pageSizeOpen]);

  return (
    <div className="w-full">

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-[12px] mb-[20px]">
        <h2
          className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[24px] tracking-[-0.5px] m-0"
          style={{ color: "var(--brand-text)" }}
        >
          Leaderboard
        </h2>
        <div className="flex items-center gap-[8px]">
          <button
            className="flex items-center gap-[6px] px-[12px] h-[32px] bg-transparent border-none cursor-pointer"
            style={{ border: "1px solid var(--brand-border)" }}
          >
            <Download size={13} style={{ color: "var(--brand-text)", opacity: 0.6 }} />
            <span
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
              style={{ color: "var(--brand-text)", opacity: 0.6 }}
            >
              Raw Data
            </span>
          </button>
          <button
            className="flex items-center gap-[6px] px-[12px] h-[32px] bg-transparent border-none cursor-pointer"
            style={{ border: "1px solid var(--brand-border)" }}
          >
            <RefreshCw size={13} style={{ color: "var(--brand-text)", opacity: 0.6 }} />
            <span
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
              style={{ color: "var(--brand-text)", opacity: 0.6 }}
            >
              Refresh
            </span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div
        className="flex items-center gap-[8px] px-[12px] h-[36px] mb-[12px]"
        style={{ border: "1px solid var(--brand-border)" }}
      >
        <Search size={13} style={{ color: "var(--brand-text)", opacity: 0.35 }} />
        <input
          type="text"
          placeholder="Search leaderboard"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="flex-1 bg-transparent border-none outline-none font-['Helvetica_Neue:Regular',sans-serif] text-[13px] placeholder-opacity-30"
          style={{ color: "var(--brand-text)" }}
        />
      </div>

      {/* Disclaimer */}
      <p
        className="font-['Helvetica_Neue:Regular',sans-serif] leading-[1.6] mb-[16px] m-0 text-[14px]"
        style={{ color: "var(--brand-text)", opacity: 0.38 }}
      >
        This leaderboard is calculated with approximately 14% of the test data. The final results will be based on the other 86%, so the final standings may be different.
      </p>

      {/* Prize contenders label */}
      <div className="flex items-center gap-[6px] mb-[4px]">
        <div className="size-[7px] rounded-full" style={{ backgroundColor: "var(--brand-text)", opacity: 0.4 }} />
        <span
          className="font-['Helvetica_Neue:Regular',sans-serif] tracking-[0.5px] text-[14px]"
          style={{ color: "var(--brand-text)", opacity: 0.4 }}
        >
          Prize Contenders
        </span>
      </div>

      {/* Table */}
      <div style={{ borderTop: "1px solid var(--brand-border)" }}>

        {/* Header */}
        <div
          className="hidden md:grid items-center py-[10px]"
          style={{
            gridTemplateColumns: "48px 320px 106px auto auto",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--brand-border-faint)",
          }}
        >
          {(["#", "Team / Player", "Members", "Score", "Last Submission"] as const).map((h, i) => (
            <div
              key={h}
              className={`font-['Helvetica_Neue:Regular',sans-serif] tracking-[0.4px] ${i >= 3 ? "text-right" : ""} text-[14px]`}
              style={{ color: "var(--brand-text)", opacity: 0.3 }}
            >
              {h}
            </div>
          ))}
        </div>

        {/* Rows */}
        {paged.map((entry) => (
          <div
            key={entry.rank}
            className="group transition-opacity hover:opacity-80"
            style={{ borderBottom: "1px solid var(--brand-border-faint)" }}
          >
            {/* Desktop */}
            <div
              className="hidden md:grid items-center py-[16px]"
              style={{ gridTemplateColumns: "48px 320px 106px auto auto", justifyContent: "space-between" }}
            >
              {/* Rank */}
              <div><RankCell rank={entry.rank} /></div>

              {/* Name + badge */}
              <div className="flex items-center gap-[8px]">
                <span
                  className="font-['Helvetica_Neue:Medium',sans-serif] text-[15px] truncate"
                  style={{ color: "var(--brand-text)" }}
                >
                  {entry.name}
                </span>
                <TypeBadge type={entry.type} />
              </div>

              {/* Members */}
              <div className="flex">
                <MemberAvatars members={entry.members} />
              </div>

              {/* Score */}
              <div className="text-right">
                <span
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] tabular-nums"
                  style={{ color: "var(--brand-text)" }}
                >
                  {entry.score.toFixed(3)}
                </span>
              </div>

              {/* Date */}
              <div className="text-right">
                <span
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]"
                  style={{ color: "var(--brand-text)", opacity: 0.45 }}
                >
                  {entry.submittedAt}
                </span>
              </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden flex items-start gap-[12px] py-[14px]">
              <div className="w-[32px] shrink-0 pt-[2px]">
                <RankCell rank={entry.rank} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-[6px] mb-[6px]">
                  <span
                    className="font-['Helvetica_Neue:Medium',sans-serif] text-[15px] truncate"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {entry.name}
                  </span>
                  <TypeBadge type={entry.type} />
                </div>
                <div className="flex items-center justify-between gap-[8px]">
                  <MemberAvatars members={entry.members} />
                  <div className="flex items-center gap-[12px]">
                    <span
                      className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] tabular-nums"
                      style={{ color: "var(--brand-text)" }}
                    >
                      {entry.score.toFixed(3)}
                    </span>
                    <span
                      className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
                      style={{ color: "var(--brand-text)", opacity: 0.4 }}
                    >
                      {entry.submittedAt}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="mt-[20px]">

        {/* Entry count row */}
        <div
          className="flex items-center h-[43px] pl-[0px]"
          style={{ borderTop: "1px solid var(--brand-border-faint)" }}
        >
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]" style={{ color: "var(--brand-text)" }}><span style={{ opacity: 0.4 }}>{rangeStart} – {rangeEnd}</span><span style={{ opacity: 0.3 }}> of {filtered.length}</span></span>
        </div>

        {/* Page nav + per-page row */}
        <div className="flex items-center justify-between h-[32px]">

          {/* Page buttons */}
          <div className="flex items-center gap-[2px]">
            {/* Prev */}
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center justify-center size-[32px] bg-transparent border-none cursor-pointer disabled:opacity-25"
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d={svgPaths.p19b17dc0} stroke="var(--brand-text)" strokeOpacity="0.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </svg>
            </button>

            {/* Page numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | "···")[]>((acc, p, idx, arr) => {
                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push("···");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "···" ? (
                  <div key={`ellipsis-${idx}`} className="flex items-center justify-center size-[32px]">
                    <span
                      className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]"
                      style={{ color: "var(--brand-text)", opacity: 0.35 }}
                    >···</span>
                  </div>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className="flex items-center justify-center size-[32px] border-none cursor-pointer rounded-[16px]"
                    style={{
                      backgroundColor: page === p ? "var(--brand-primary-btn-bg)" : "transparent",
                    }}
                  >
                    <span
                      className={`font-['Helvetica_Neue:${page === p ? "Medium" : "Regular"}',sans-serif] text-[13px]`}
                      style={{
                        color: page === p ? "var(--brand-primary-btn-text)" : "var(--brand-text)",
                        opacity: page === p ? 1 : 0.6,
                      }}
                    >{p}</span>
                  </button>
                )
              )}

            {/* Next */}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center justify-center size-[32px] bg-transparent border-none cursor-pointer disabled:opacity-25"
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d={svgPaths.p5646280} stroke="var(--brand-text)" strokeOpacity="0.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </svg>
            </button>
          </div>

          {/* Entries per page dropdown */}
          <div ref={pageSizeRef} className="relative">
            <button
              onClick={() => setPageSizeOpen(o => !o)}
              className="flex items-center gap-[8px] px-[12px] h-[32px] bg-transparent cursor-pointer"
              style={{ border: "1px solid rgba(59,14,39,0.12)" }}
            >
              <span
                className="font-['Helvetica_Neue:Medium',sans-serif] text-[13px]"
                style={{ color: "rgba(59,14,39,0.65)" }}
              >
                {pageSize} entries per page
              </span>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d={svgPaths.pa029e20} stroke="var(--brand-text)" strokeOpacity="0.65" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.08333" />
              </svg>
            </button>

            {pageSizeOpen && (
              <div
                className="absolute right-0 bottom-[calc(100%+4px)] z-10 min-w-full"
                style={{ border: "1px solid rgba(59,14,39,0.12)", backgroundColor: "var(--brand-bg)" }}
              >
                {PAGE_SIZES.map(size => (
                  <button
                    key={size}
                    onClick={() => handlePageSize(size)}
                    className="w-full flex items-center px-[12px] h-[32px] bg-transparent border-none cursor-pointer text-left"
                    style={{
                      backgroundColor: pageSize === size ? "rgba(59,14,39,0.03)" : "transparent",
                    }}
                  >
                    <span
                      className={`font-['Helvetica_Neue:${pageSize === size ? "Medium" : "Regular"}',sans-serif] text-[13px] whitespace-nowrap`}
                      style={{ color: "var(--brand-text)", opacity: pageSize === size ? 1 : 0.6 }}
                    >
                      {size} entries per page
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}